import { useMutation } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';
import http from '@/services/http';
import { AxiosError } from 'axios';
import { setTokens } from '@/services/tokenStorage';

interface LoginData {
  email: string;
  password: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  refresh_expires_at: number;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

interface ForgotPasswordData {
  email: string;
}

interface ResetPasswordData {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

interface VerifyEmailData {
  id: string;
  hash: string;
  expires: string;
  signature: string;
}

export function useLogin() {
  return useMutation({
    mutationFn: async (data: LoginData) => {
      const response = await http.post<TokenResponse>('/api/login', data);
      // Store tokens from response
      if (response.data.access_token && response.data.refresh_token) {
        setTokens(response.data.access_token, response.data.refresh_token);
      }
      return response;
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 422) {
        // Handle specific error format for login
        const errorData = error.response.data;
        if (errorData.message) {
          toast.error(errorData.message);
        } else {
          throw error; // Re-throw for component handling if it's validation errors
        }
      } else {
        toast.error('These credentials do not match our records.');
      }
    }
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (data: RegisterData) => {
      return http.post('/api/register', data);
    },
    onSuccess: () => {
      toast.success('Registration successful. Please check your email to verify your account.');
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 422) {
        throw error; // Re-throw to let component handle validation errors
      } else {
        toast.error('There was an error during registration.');
      }
    }
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: ForgotPasswordData) => {
      return http.post('/api/forgot-password', data);
    },
    onSuccess: () => {
      toast.success('Password reset link sent to your email.');
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 422) {
        // Handle specific error format for forgot password
        const errorData = error.response.data;
        if (errorData.message) {
          toast.error(errorData.message);
        } else {
          throw error; // Re-throw for component handling if it's validation errors
        }
      } else {
        toast.error('There was an error sending the password reset link.');
      }
    }
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: ResetPasswordData) => {
      return http.post('/api/reset-password', data);
    },
    onSuccess: () => {
      toast.success('Password reset successful. You can now login with your new password.');
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 422) {
        throw error; // Re-throw to let component handle validation errors
      } else {
        toast.error('There was an error resetting your password.');
      }
    }
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (data: VerifyEmailData) => {
      return http.get(`/api/verify-email/${data.id}/${data.hash}`, {
        params: { expires: data.expires, signature: data.signature }
      });
    },
    onSuccess: () => {
      toast.success('Email verified successfully!');
    },
    onError: () => {
      toast.error('Failed to verify email. The link may have expired.');
    }
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: () => {
      return http.post('/api/email/verification-notification');
    },
    onSuccess: () => {
      toast.success('Verification link has been sent to your email.');
    },
    onError: () => {
      toast.error('Failed to send verification email. Please try again.');
    }
  });
} 